import { PlusOutlined } from "@ant-design/icons";
import {
	Button,
	Card,
	Checkbox,
	Empty,
	Input,
	InputNumber,
	Popconfirm,
	Space,
	Table,
	Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePositionsList } from "@/modules/positions/hooks/usePositions";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { usePermission } from "@/shared/hooks/usePermission";
import {
	useCreateDepartmentGrade,
	useDeleteDepartmentGrade,
	useDepartmentGradesList,
	useSetDepartmentGradePositions,
	useUpdateDepartmentGrade,
} from "../hooks/useDepartmentGrades";
import type { DepartmentGrade } from "../types";

const { Text } = Typography;

/**
 * Tanlangan darajaning nom/daraja tahriri — review Important 3: migratsiya
 * darajalarga `"N-daraja (avtomatik, tahrirlang)"` nomini yozadi, lekin
 * UI'da uni o'zgartirib bo'lmagan. `key={grade.id}` bilan chaqiriladi (parent
 * komponentda) — shu sabab holat tanlash o'zgarganda `useEffect`siz o'zi
 * tiklanadi.
 *
 * ⚠️⚠️ «Saqlash» (`department-grades:update`) va «O'chirish»
 * (`department-grades:delete`) MUSTAQIL ruxsatlar — biri ikkinchisini
 * talab qilmaydi. Ruxsat bo'lmasa tugma UMUMAN chizilmaydi, `disabled`
 * QILINMAYDI.
 *
 * ⚠️ Backend 409 qaytarsa (ishlatilayotgan darajaning `level` i
 * o'zgartirilgan yoki o'chirilgan — `departmentGrades.levelInUse`), aniq
 * xabar `useUpdateDepartmentGrade`/`useDeleteDepartmentGrade` ichida
 * `getErrorMessage` orqali ko'rsatiladi — bu yerda qo'shimcha `onError`
 * kerak emas.
 */
function GradeEditor({
	grade,
	canEdit,
	canDelete,
	onDeleted,
}: {
	grade: DepartmentGrade;
	canEdit: boolean;
	canDelete: boolean;
	onDeleted: () => void;
}) {
	const { t } = useTranslation("positions");
	const updateGrade = useUpdateDepartmentGrade();
	const deleteGrade = useDeleteDepartmentGrade();
	const [level, setLevel] = useState<number | null>(grade.level);
	const [name, setName] = useState(grade.name);

	return (
		<div className="mb-4 flex items-center justify-between gap-2 flex-wrap">
			{canEdit ? (
				<Space wrap>
					<InputNumber value={level} onChange={setLevel} style={{ width: 90 }} />
					<Input value={name} onChange={(e) => setName(e.target.value)} style={{ width: 160 }} />
					<Button
						type="primary"
						loading={updateGrade.isPending}
						disabled={level === null || name.trim().length === 0}
						onClick={() => {
							if (level === null) {
								return;
							}
							updateGrade.mutate({ id: grade.id, input: { level, name } });
						}}
					>
						{t("grades.saveButton")}
					</Button>
				</Space>
			) : (
				<Text strong>{`${grade.level} · ${grade.name}`}</Text>
			)}
			{canDelete ? (
				<Popconfirm
					title={t("grades.deleteConfirm.title")}
					description={t("grades.deleteConfirm.description")}
					okText={t("grades.deleteConfirm.okText")}
					cancelText={t("grades.deleteConfirm.cancelText")}
					onConfirm={() => deleteGrade.mutate(grade.id, { onSuccess: onDeleted })}
				>
					<Button danger loading={deleteGrade.isPending}>
						{t("grades.delete")}
					</Button>
				</Popconfirm>
			) : null}
		</div>
	);
}

export function GradeCatalogTab() {
	const { t } = useTranslation("positions");
	const { can } = usePermission();
	const { data: grades = [], isLoading } = useDepartmentGradesList();
	const { data: allPositions = [] } = usePositionsList();
	const createGrade = useCreateDepartmentGrade();
	const setPositions = useSetDepartmentGradePositions();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [newLevel, setNewLevel] = useState<number | null>(null);
	const [newName, setNewName] = useState("");

	const selected = useMemo(
		() => grades.find((grade) => grade.id === selectedId) ?? null,
		[grades, selectedId]
	);
	const selectedPositionIds = useMemo(
		() => new Set(selected?.positions.map((position) => position.id) ?? []),
		[selected]
	);

	const canEditPositions = can("department-grades", "update");
	const canDeleteGrade = can("department-grades", "delete");

	const columns: ColumnsType<DepartmentGrade> = [
		{ title: t("grades.columns.level"), dataIndex: "level", key: "level", width: 90 },
		{ title: t("grades.columns.name"), dataIndex: "name", key: "name" },
		{
			title: t("grades.columns.positions"),
			key: "positions",
			render: (_, grade) => grade.positions.length,
		},
		{
			title: t("grades.columns.actions"),
			key: "actions",
			align: "right",
			render: (_, grade) => (
				<Button type="link" onClick={() => setSelectedId(grade.id)}>
					{t("grades.edit")}
				</Button>
			),
		},
	];

	return (
		<div className="flex gap-6 flex-wrap items-start">
			<Card title={t("grades.title")} className="flex-1 min-w-96">
				<PermissionGate resource="department-grades" action="create">
					<Space className="mb-4">
						<InputNumber
							placeholder={t("grades.levelPlaceholder")}
							value={newLevel}
							onChange={(value) => setNewLevel(value)}
						/>
						<Input
							placeholder={t("grades.namePlaceholder")}
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
						/>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							disabled={newLevel === null || newName.trim().length === 0}
							loading={createGrade.isPending}
							onClick={() => {
								if (newLevel === null) {
									return;
								}
								createGrade.mutate(
									{ level: newLevel, name: newName },
									{
										onSuccess: () => {
											setNewLevel(null);
											setNewName("");
										},
									}
								);
							}}
						>
							{t("grades.createButton")}
						</Button>
					</Space>
				</PermissionGate>
				<Table<DepartmentGrade>
					rowKey="id"
					columns={columns}
					dataSource={grades}
					loading={isLoading}
					pagination={false}
				/>
			</Card>

			<Card
				title={selected ? `${selected.level} · ${selected.name}` : t("grades.selectTitle")}
				className="w-80"
			>
				{selected ? (
					<div className="flex flex-col gap-2">
						<GradeEditor
							key={selected.id}
							grade={selected}
							canEdit={canEditPositions}
							canDelete={canDeleteGrade}
							onDeleted={() => setSelectedId(null)}
						/>
						<Text type="secondary" className="text-xs">
							{t("grades.positionsLabel")}
						</Text>
						{allPositions.map((position) => (
							<Checkbox
								key={position.id}
								disabled={!canEditPositions || setPositions.isPending}
								checked={selectedPositionIds.has(position.id)}
								onChange={(e) => {
									const next = new Set(selectedPositionIds);
									if (e.target.checked) {
										next.add(position.id);
									} else {
										next.delete(position.id);
									}
									setPositions.mutate({ id: selected.id, positionIds: [...next] });
								}}
							>
								{position.name}
							</Checkbox>
						))}
					</div>
				) : (
					<Empty description={t("grades.selectPrompt")} />
				)}
			</Card>
		</div>
	);
}
